import React, { useState } from 'react';
import { ChevronUpIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import CommentsHeroBanner from './CommentsHeroBanner';
import CommentComposer from './CommentComposer';
import CommentList from './CommentList';
import { useComments } from '../../hooks/comments/useComments';
import { useCommentSocket } from '../../hooks/comments/useCommentSocket';
import { useCreateComment } from '../../hooks/comments/useCommentMutations';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import type { CommentTargetType } from '../../types/comments';

interface CommentsSectionProps {
  targetType: CommentTargetType;
  targetId: string;
}

/**
 * Self-contained Comments surface: collapsible header → mascot banner →
 * composer → list. Wires the realtime socket and mutations under the hood.
 */
export default function CommentsSection({ targetType, targetId }: CommentsSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const reduceMotion = useReducedMotion();
  const identity = useCurrentChatIdentity();
  const viewerId = identity.userId;
  const viewerName = identity.fullName;
  const viewerAvatar = identity.avatarUrl;
  const viewerRole = identity.variant === 'learner' ? 'learner' : null;

  const {
    comments,
    isLoading,
    isError,
    isForbidden,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useComments(targetType, targetId, true);

  // Skip the WS subscription when access is denied.
  useCommentSocket(targetType, targetId, !isForbidden);

  const createComment = useCreateComment();

  const handlePost = async (body: string) => {
    if (!viewerId || !viewerName) return;
    await createComment.mutateAsync({
      targetType,
      targetId,
      body,
      author: {
        id: viewerId,
        name: viewerName,
        avatar_url: viewerAvatar,
        role: viewerRole,
      },
    });
  };

  const totalLabel = comments.length + (hasNextPage ? '+' : '');

  return (
    <section
      className="rounded-3xl border border-border-primary/60 bg-surface-primary overflow-hidden"
      aria-label="Comments"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        className="flex items-center w-full gap-2 h-14 px-4 sm:px-6 text-left hover:bg-[rgba(3,2,19,0.02)] dark:hover:bg-white/[0.02] transition-colors duration-fast ease-out-strong"
      >
        <ChatBubbleLeftIcon className="w-5 h-5 text-primary-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-text-primary tracking-wide uppercase">
          Comments
        </span>
        <span className="text-sm text-text-tertiary">({isLoading ? '…' : totalLabel})</span>
        <ChevronUpIcon
          className={`ml-auto w-5 h-5 text-text-tertiary transition-transform duration-200 ease-out-strong ${
            collapsed ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="body"
            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            style={{ overflow: 'hidden' }}
          >
            {isForbidden ? (
              <ForbiddenState />
            ) : (
              <>
                <CommentsHeroBanner targetType={targetType} />
                <div className="bg-[rgba(247,242,240,0.5)] dark:bg-white/[0.02] border-t border-border-primary/40 px-4 sm:px-6 py-5 space-y-5">
                  <CommentComposer
                    enabled={!!viewerId}
                    onSubmit={handlePost}
                    isPending={createComment.isPending}
                  />
                  <div className="border-t border-border-primary/40 pt-5">
                    <CommentList
                      comments={comments}
                      targetType={targetType}
                      targetId={targetId}
                      viewerId={viewerId}
                      viewerName={viewerName}
                      viewerAvatar={viewerAvatar}
                      viewerRole={viewerRole}
                      isLoading={isLoading}
                      isError={isError}
                      hasNextPage={hasNextPage}
                      isFetchingNextPage={isFetchingNextPage}
                      fetchNextPage={fetchNextPage}
                    />
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ForbiddenState() {
  return (
    <div className="px-6 py-12 text-center space-y-2">
      <ChatBubbleLeftIcon className="w-10 h-10 text-text-tertiary mx-auto" aria-hidden="true" />
      <p className="text-sm text-text-secondary">
        You don't have access to this conversation.
      </p>
    </div>
  );
}
